import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { MdDownload, MdFilterList, MdViewColumn } from 'react-icons/md';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';

function getNestedValue(obj, accessor) {
    return accessor.split('.').reduce((acc, key) => acc?.[key], obj);
}

export default function DataTable({
    actions,
    columns,
    data,
    placeholder,
    onRowSelect,
    selectedRows,
    onSelectionChange,
    enableSelection,
    accessorKey,
    enableActions,
    tableName,
    enablePagination = false,
    pageSizeOptions = [5, 10, 25, 50],
}) {
    const [searchQuery, setSearchQuery] = useState('');
    const storageId = React.useMemo(() => {
        if (tableName) return tableName;
        if (typeof window !== 'undefined' && window.location && window.location.href) return encodeURIComponent(window.location.href);
        return 'default-table';
    }, [tableName]);

    const [sortConfig, setSortConfig] = useState(() => {
        try {
            const saved = localStorage.getItem(`${storageId}-sortConfig`);
            return saved ? JSON.parse(saved) : null;
        } catch (e) {
            return null;
        }
    });
    const [resizingCol, setResizingCol] = useState(null);
    const [columnWidths, setColumnWidths] = useState(() => {
        try {
            const saved = localStorage.getItem(`${storageId}-columnWidths`);
            return saved ? JSON.parse(saved) : {};
        } catch (e) {
            return {};
        }
    });

    // Per-column filter state: accessor -> Set of allowed values (as strings)
    const [columnFilters, setColumnFilters] = useState({});
    // Which column's filter menu is open
    const [openFilter, setOpenFilter] = useState(null);
    const headerRefs = useRef({});
    const portalDivRef = useRef(null);
    const [menuCoords, setMenuCoords] = useState(null);
    const [showColumnSelector, setShowColumnSelector] = useState(false);
    const columnSelectorRef = useRef(null);

    const [visibleColumns, setVisibleColumns] = useState(() => {
        const initial = {};
        (columns || []).forEach((c) => { initial[c.accessor] = true; });
        return initial;
    });
    // search term inside the open filter menu
    const [filterSearch, setFilterSearch] = useState('');

    const sortedData = React.useMemo(() => {
        if (!sortConfig) return data;
        return [...data].sort((a, b) => {
            const aValue = getNestedValue(a, sortConfig.key);
            const bValue = getNestedValue(b, sortConfig.key);
            if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    }, [data, sortConfig]);

    // Apply global search first, then per-column filters
    const filteredData = React.useMemo(() => {
        const q = searchQuery?.toLowerCase() || '';
        let res = sortedData?.filter((row) => JSON.stringify(row).toLowerCase().includes(q));

        // Apply column filters (if any)
        const filters = columnFilters || {};
        const filterKeys = Object.keys(filters).filter((k) => filters[k] && filters[k].size > 0);
        if (filterKeys.length === 0) return res;

        res = res.filter((row) => {
            return filterKeys.every((accessor) => {
                const allowed = filters[accessor];
                const val = getNestedValue(row, accessor);
                // stringify to compare consistently
                const str = val === null || val === undefined ? '' : String(val);
                return allowed.has(str);
            });
        });

        return res;
    }, [sortedData, searchQuery, columnFilters]);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(pageSizeOptions[1] || 10);

    useEffect(() => {
        // Reset to first page whenever the filter or pageSize changes
        setCurrentPage(1);
    }, [searchQuery, pageSize]);

    const totalItems = filteredData?.length || 0;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

    // Ensure currentPage is within bounds
    useEffect(() => {
        if (currentPage > totalPages) setCurrentPage(totalPages);
    }, [currentPage, totalPages]);

    const paginatedData = enablePagination
        ? filteredData?.slice((currentPage - 1) * pageSize, currentPage * pageSize)
        : filteredData;

    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig?.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        const newSortConfig = { key, direction };
        setSortConfig(newSortConfig);
        try {
            localStorage.setItem(`${storageId}-sortConfig`, JSON.stringify(newSortConfig));
        } catch (e) {}
    };

    const getSortIcon = (key) => {
        if (!sortConfig || sortConfig.key !== key) return <FaSort className="ml-1 text-sm text-gray-500" />;
        return sortConfig.direction === 'asc' ? <FaSortUp className="ml-1 text-sm text-gray-700" /> : <FaSortDown className="ml-1 text-sm text-gray-700" />;
    };

    const handleExport = () => {
        const csv = [columns.map((col) => col.header).join(',')];
        sortedData.forEach((row) => {
            csv.push(columns.map((col) => getNestedValue(row, col.accessor)).join(','));
        });
        const blob = new Blob([csv.join('\n')], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'table-data.csv';
        a.click();
        URL.revokeObjectURL(url);
    };

    const handleMouseDown = (accessor) => (e) => {
        setResizingCol(accessor);
    };

    

    const handleMouseMove = React.useCallback((e) => {
        if (resizingCol) {
            const newWidths = { ...columnWidths };
            const th = document.querySelector(`th[data-accessor='${resizingCol}']`);
            if (th) {
                const newWidth = e.clientX - th.getBoundingClientRect().left;
                newWidths[resizingCol] = Math.max(newWidth, 40);
                setColumnWidths(newWidths);
                try {
                    localStorage.setItem(`${storageId}-columnWidths`, JSON.stringify(newWidths));
                } catch (e) {}
            }
        }
    }, [resizingCol, columnWidths, storageId]);

    const handleMouseUp = React.useCallback(() => {
        setResizingCol(null);
    }, []);

    const getUniqueValuesForColumn = (accessor) => {
        const set = new Set();
        (data || []).forEach((row) => {
            const v = getNestedValue(row, accessor);
            set.add(v === null || v === undefined ? '' : String(v));
        });
        return Array.from(set).sort();
    };

    const toggleFilterValue = (accessor, value) => {
        setColumnFilters((prev) => {
            const next = { ...(prev || {}) };
            const current = new Set(next[accessor] ? Array.from(next[accessor]) : []);
            if (current.has(value)) current.delete(value);
            else current.add(value);
            next[accessor] = current;
            return next;
        });
    };

    const clearFilter = (accessor) => {
        setColumnFilters((prev) => {
            const next = { ...(prev || {}) };
            delete next[accessor];
            return next;
        });
    };

    useEffect(() => {
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        const onDocClick = (e) => {
            // close filter menu if click outside (header or portal)
            if (openFilter) {
                const headerEl = headerRefs.current[openFilter];
                const portalEl = portalDivRef.current;
                if (headerEl && headerEl.contains(e.target)) return;
                if (portalEl && portalEl.contains(e.target)) return;
                setOpenFilter(null);
            }
        };
        document.addEventListener('click', onDocClick);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            document.removeEventListener('click', onDocClick);
        };
    }, [handleMouseMove, handleMouseUp, openFilter]);

    // create portal container
    useEffect(() => {
        const div = document.createElement('div');
        div.style.position = 'absolute';
        div.style.top = '0';
        div.style.left = '0';
        div.style.width = '0';
        div.style.height = '0';
        div.style.zIndex = '9999';
        document.body.appendChild(div);
        portalDivRef.current = div;
        return () => {
            if (portalDivRef.current) document.body.removeChild(portalDivRef.current);
            portalDivRef.current = null;
        };
    }, []);

    // compute menu coords whenever openFilter changes
    useEffect(() => {
        if (!openFilter) {
            setMenuCoords(null);
            setFilterSearch('');
            return;
        }
        const th = headerRefs.current[openFilter];
        if (!th) return;
        const rect = th.getBoundingClientRect();
        // position menu below header, align right edge to header's right edge
        setMenuCoords({ top: rect.bottom + window.scrollY + 6, left: rect.right + window.scrollX - 220 });
    }, [openFilter]);

    // Pagination display helpers
    const startIndex = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const endIndex = Math.min(currentPage * pageSize, totalItems);

    // Responsive: switch to card view on small screens
    const [isMobile, setIsMobile] = useState(() => {
        if (typeof window !== 'undefined') return window.innerWidth < 768;
        return false;
    });

    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    return (
        <div className="overflow-x-auto w-full">
            <div className="flex justify-between items-center mb-4">
                {enableActions && (
                    <>
                        <input
                            className="w-[500px] px-3 py-2 text-xs border border-gray-300 rounded focus:outline-none focus:none"
                            type="text"
                            placeholder="Search..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        <div className="flex flex-row items-center gap-2">
                            {/* show selected count + bulk action select when selection enabled */}
                            {enableSelection && (selectedRows?.length > 0) && (
                                <div className="flex items-center gap-2">
                                    <div className="text-sm text-gray-700">{selectedRows.length} selected</div>
                                    {/* <pre>{JSON.stringify(selectedRows, null, 2)}</pre> */}
                                    <select
                                        className="text-sm border rounded px-2 py-1"
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            // bubble up the action via onSelectionChange if provided
                                            if (onSelectionChange) onSelectionChange({ action: val, selected: selectedRows });
                                            // reset select
                                            e.target.selectedIndex = 0;
                                        }}
                                    >
                                        <option value="">Actions</option>
                                        <option value="upload-image">Upload Image</option>
                                    </select>
                                </div>
                            )}
                            <div className="relative group">
                                <button className="size-10 rounded-full hover:bg-gray-100 flex flex-col justify-center items-center" name="Export as CSV" onClick={handleExport}>
                                    <MdDownload className="text-base fill-black" />
                                </button>
                            </div>
                            <div className="relative group">
                                <button
                                    className="size-10 rounded-full hover:bg-gray-100 flex flex-col justify-center items-center"
                                    onClick={() => setShowColumnSelector((s) => !s)}
                                    aria-label="Choose columns"
                                >
                                    <MdViewColumn className="text-base fill-black" />
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {isMobile ? (
                <div className="flex flex-col gap-3">
                    {paginatedData?.length > 0 ? (
                        paginatedData.map((row, index) => (
                            <div key={index} className="border rounded p-3 bg-white shadow-sm">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="flex items-center gap-2">
                                        {enableSelection && (
                                            <input
                                                type="checkbox"
                                                className="form-checkbox h-4 w-4 text-blue-600"
                                                checked={selectedRows?.includes(getNestedValue(row, accessorKey))}
                                                onChange={() => {
                                                    if (typeof(onRowSelect) === 'function') onRowSelect(getNestedValue(row, accessorKey));
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        )}
                                        <div className="flex flex-col">
                                            {(columns || []).filter(c => visibleColumns[c.accessor]).map((column) => (
                                                <div key={column.accessor} className="text-sm text-gray-700">
                                                    <div className="text-xs text-gray-500">{column.header}</div>
                                                    <div className="font-medium">{column.data ? column.data(row) : (column.modifier ? column.modifier(getNestedValue(row, column.accessor), row) : (getNestedValue(row, column.accessor) || '-'))}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {actions && (
                                        <div className="ml-2">
                                            {actions(row)}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="text-center py-6 text-gray-500">{placeholder || 'No data available'}</div>
                    )}
                </div>
            ) : (
                <table className="min-w-full divide-y divide-gray-200 border card">
                    <thead className="bg-gray-100 text-xs font-semibold text-gray-700">
                        <tr>
                            {enableSelection && (
                                <th className="p-3 text-left">
                                    <input
                                        type="checkbox"
                                        className="form-checkbox h-4 w-4 text-blue-600"
                                        checked={selectedRows && selectedRows.length === (paginatedData?.length || 0) && paginatedData?.length > 0}
                                        onChange={(e) => {
                                            // select all on current page
                                            if (e.target.checked) {
                                                const ids = (paginatedData || []).map((r) => getNestedValue(r, accessorKey));
                                                if (onSelectionChange) onSelectionChange({ action: 'set', selected: ids });
                                                if (onRowSelect) ids.forEach((id) => onRowSelect(id));
                                            } else {
                                                if (onSelectionChange) onSelectionChange({ action: 'clear' });
                                                if (onRowSelect) paginatedData.forEach((r) => onRowSelect(getNestedValue(r, accessorKey)));
                                            }
                                        }}
                                    />
                                </th>
                            )}
                            {columns.filter(c => visibleColumns[c.accessor]).map((column) => (
                                <th
                                    key={column.accessor}
                                    data-accessor={column.accessor}
                                    style={{ width: columnWidths[column.accessor] || 'auto' }}
                                    className="p-3 relative select-none cursor-pointer text-left whitespace-nowrap"
                                    ref={(el) => (headerRefs.current[column.accessor] = el)}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center">
                                            <span onClick={() => requestSort(column.accessor)} className="select-none">{column.header}</span>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); requestSort(column.accessor); }}
                                                className="p-1 rounded hover:bg-gray-100"
                                                aria-label={`Sort ${column.header}`}
                                            >
                                                {getSortIcon(column.accessor)}
                                            </button>

                                            <button
                                                type="button"
                                                className={`p-1 rounded hover:bg-gray-200 flex items-center gap-1 ${columnFilters?.[column.accessor]?.size > 0 ? 'bg-teal-50' : ''}`}
                                                onClick={(e) => { e.stopPropagation(); setOpenFilter(openFilter === column.accessor ? null : column.accessor); }}
                                                aria-label={`Filter ${column.header}`}
                                            >
                                                <MdFilterList className={`text-sm ${columnFilters?.[column.accessor]?.size > 0 ? 'text-teal-600' : 'text-gray-600'}`} />
                                                {columnFilters?.[column.accessor]?.size > 0 && (
                                                    <span className="w-2 h-2 rounded-full bg-teal-600 inline-block" aria-hidden></span>
                                                )}
                                            </button>

                                            <div
                                                className="absolute right-0 top-0 h-full w-1 cursor-col-resize"
                                                onMouseDown={handleMouseDown(column.accessor)}
                                            ></div>
                                        </div>
                                    </div>
                                </th>
                            ))}
                            {actions && <th className="p-3 text-left">Actions</th>}
                        </tr>
                    </thead>
                    <tbody className="text-sm divide-y divide-gray-100">
                        {paginatedData?.length > 0 ? (
                            paginatedData?.map((row, index) => (
                                <tr
                                    key={index}
                                    className="hover:bg-gray-50 transition cursor-pointer"
                                    onClick={() => enableSelection && typeof onRowSelect === 'function' && onRowSelect(getNestedValue(row, accessorKey))}
                                >
                                    {enableSelection && (
                                        <td className="p-3">
                                            <input
                                                type="checkbox"
                                                className="form-checkbox h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                checked={selectedRows?.includes(getNestedValue(row, accessorKey))}
                                                onChange={() => { if (typeof onRowSelect === 'function') onRowSelect(getNestedValue(row, accessorKey)); }}
                                            />
                                        </td>
                                    )}
                                    {columns.filter(c => visibleColumns[c.accessor]).map((column) => (
                                        <td key={column.accessor} className="p-3">
                                            {column.data
                                                ? column.data(row)
                                                : column.modifier
                                                    ? column.modifier(getNestedValue(row, column.accessor), row)
                                                    : getNestedValue(row, column.accessor)
                                                        ? getNestedValue(row, column.accessor)
                                                        : '-'}
                                        </td>
                                    ))}
                                    {actions && <td className="p-3">{actions(row)}</td>}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={columns.length + (enableSelection ? 2 : 1)} className="text-center py-6 text-gray-500">
                                    {placeholder || 'No data available'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            )}
            {enablePagination && (
                <div className="flex items-center justify-between mt-3">
                    <div className="text-sm text-gray-600">
                        Showing {startIndex} to {endIndex} of {totalItems}
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            className="px-3 py-1 bg-white border rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            aria-label="Previous page"
                        >
                            Prev
                        </button>

                        <div className="px-3 py-1 border rounded-md bg-gray-50 text-sm">{currentPage} / {totalPages}</div>

                        <button
                            className="px-3 py-1 bg-white border rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                            aria-label="Next page"
                        >
                            Next
                        </button>
                    </div>

                    <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-600">Rows per page:</label>
                        <div className="relative">
                            <select
                                className="appearance-none border border-gray-200 rounded-md px-3 py-1 text-sm bg-white pr-8 focus:outline-none focus:ring-2 focus:ring-teal-200"
                                value={pageSize}
                                onChange={(e) => setPageSize(Number(e.target.value))}
                                aria-label="Rows per page"
                            >
                                {pageSizeOptions.map((s) => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>

                            <svg className="pointer-events-none absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </div>
                    </div>
                </div>
            )}
            {/* portal-based filter menu */}
            {portalDivRef.current && openFilter && menuCoords && createPortal(
                <div
                    className="bg-white border rounded shadow-md z-50"
                    style={{ position: 'absolute', top: menuCoords.top + 'px', left: menuCoords.left + 'px', minWidth: 220, maxHeight: '60vh', overflow: 'auto' }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="p-2 text-sm font-semibold border-b">Filter {columns.find(c=>c.accessor===openFilter)?.header}</div>
                    <div className="p-2">
                        <input
                            type="search"
                            value={filterSearch}
                            onChange={(e) => setFilterSearch(e.target.value)}
                            placeholder="Search..."
                            className="w-full px-2 py-1 border rounded text-sm mb-2"
                        />

                        {getUniqueValuesForColumn(openFilter)
                            .filter((v) => v.toLowerCase().includes(filterSearch.toLowerCase()))
                            .map((val) => {
                                const checked = (columnFilters?.[openFilter]?.has(val));
                                return (
                                    <label key={val} className="flex items-center gap-2 py-1 text-sm">
                                        <input type="checkbox" checked={!!checked} onChange={() => toggleFilterValue(openFilter, val)} />
                                        <span className="truncate">{val === '' ? '(empty)' : val}</span>
                                    </label>
                                );
                            })}
                    </div>
                    <div className="flex items-center justify-between p-2 border-t">
                        <button className="text-xs text-gray-600 underline" onClick={() => clearFilter(openFilter)}>Clear</button>
                        <button className="text-xs bg-teal-500 text-white px-2 py-1 rounded" onClick={() => setOpenFilter(null)}>Done</button>
                    </div>
                </div>
            , portalDivRef.current)}
            {/* column selector portal */}
            {portalDivRef.current && showColumnSelector && createPortal(
                <div
                    className="bg-white border rounded shadow-md z-50"
                    style={{ position: 'absolute', top: 80, left: window.innerWidth - 260, minWidth: 220, maxHeight: '60vh', overflow: 'auto' }}
                    onClick={(e) => e.stopPropagation()}
                    ref={columnSelectorRef}
                >
                    <div className="p-2 text-sm font-semibold border-b">Choose columns</div>
                    <div className="p-2">
                        {(columns || []).map((col) => (
                            <label key={col.accessor} className="flex items-center gap-2 py-1 text-sm">
                                <input
                                    type="checkbox"
                                    checked={!!visibleColumns[col.accessor]}
                                    onChange={() => setVisibleColumns((prev) => ({ ...prev, [col.accessor]: !prev[col.accessor] }))}
                                />
                                <span className="truncate">{col.header}</span>
                            </label>
                        ))}
                    </div>
                    <div className="flex items-center justify-between p-2 border-t">
                        <button className="text-xs text-gray-600 underline" onClick={() => {
                            const next = {};
                            (columns || []).forEach(c => next[c.accessor] = true);
                            setVisibleColumns(next);
                        }}>Select all</button>
                        <button className="text-xs text-gray-600 underline" onClick={() => {
                            const next = {};
                            (columns || []).forEach(c => next[c.accessor] = false);
                            setVisibleColumns(next);
                        }}>Clear</button>
                    </div>
                </div>
            , portalDivRef.current)}
        </div>
    );
}

// Note: portal rendering moved outside main return to keep JSX clear
